<template>
  <v-dialog v-model="show" max-width="700px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ title }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-3 pb-2">
        <div
          ref="editor"
          class="json-editor"
          contenteditable="true"
          spellcheck="false"
          @input="onEditorInput"
        />
        <span v-if="jsonError" class="caption error--text">
          {{ jsonError }}
        </span>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn flat color="primary" @click="onCancel">Cancelar</v-btn>
        <v-btn color="primary" dark :disabled="hasJsonError" @click="onApply">
          Aplicar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Editar JSON",
    },
    initialJson: {
      type: [Object, Array, String],
      default: null,
    },
  },

  data() {
    return {
      rawJson: "",
      jsonError: "",
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    hasJsonError() {
      return !!this.jsonError;
    },
  },

  watch: {
    value(isOpen) {
      if (!isOpen) return;
      this.jsonError = "";
      this.rawJson = this.formatJson(this.initialJson);
      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.innerText = this.rawJson;
        }
      });
    },
  },

  methods: {
    formatJson(json) {
      if (json === null || json === undefined) return "";
      if (typeof json === "string") {
        try {
          return JSON.stringify(JSON.parse(json), null, 2);
        } catch {
          return json;
        }
      }
      try {
        return JSON.stringify(json, null, 2);
      } catch {
        return "";
      }
    },

    onEditorInput(event) {
      this.rawJson = event.target.innerText;
      try {
        const jsonWithDoubleQuotes = this.rawJson.replace(/'/g, '"');
        JSON.parse(jsonWithDoubleQuotes);
        this.jsonError = "";
      } catch (e) {
        this.jsonError = this.translateJsonError(e.message);
      }
    },

    translateJsonError(errorMessage) {
      const translations = {
        "Unexpected token": "Token inesperado",
        "Unexpected end of JSON input": "Fim inesperado do JSON",
        "Unexpected string in JSON": "String inesperada no JSON",
        "Unexpected number in JSON": "Número inesperado no JSON",
        "Unexpected boolean in JSON": "Booleano inesperado no JSON",
        "Unexpected null in JSON": "Nulo inesperado no JSON",
        "Unexpected character in JSON": "Caractere inesperado no JSON",
        "Unexpected identifier in JSON": "Identificador inesperado no JSON",
        "Unexpected keyword in JSON": "Palavra-chave inesperada no JSON",
        "Unexpected token < in JSON": "Token inesperado < no JSON",
      };

      for (const [key, value] of Object.entries(translations)) {
        if (errorMessage.includes(key)) {
          return errorMessage.replace(key, value);
        }
      }

      return "Erro no JSON";
    },

    onApply() {
      if (this.hasJsonError) return;
      try {
        const jsonWithDoubleQuotes = this.rawJson.replace(/'/g, '"');
        const parsed = JSON.parse(jsonWithDoubleQuotes);
        this.$emit("apply", parsed);
        this.$emit("input", false);
      } catch (e) {
        this.jsonError = this.translateJsonError(e.message);
      }
    },

    onCancel() {
      this.$emit("cancel");
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.json-editor {
  background: #1e1e1e;
  color: #d4d4d4;
  font-family: "Courier New", Courier, monospace;
  font-size: 13px;
  line-height: 1.6;
  padding: 16px;
  border-radius: 4px;
  min-height: 300px;
  max-height: 500px;
  overflow: auto;
  outline: none;
  white-space: pre;
  tab-size: 2;
}
</style>
